/**
 * Analytics Service Implementation
 * 
 * Real implementation of the analytics service that connects to the backend API.
 */

import { BaseService } from './base/BaseService';
import { IAnalyticsService } from '../interfaces/IAnalyticsService';
import { 
  Dashboard, 
  Metric, 
  Report, 
  DataVisualization,
  Visualization,
  FeaturedDashboardsResponse
} from '../../types/analytics';
import { PaginatedResponse, TimeRange, FilterCriteria } from '../../types/common';

export class AnalyticsService extends BaseService implements IAnalyticsService {
  // Updated to include type and church_id in updates
  private analyticsUrl: string;

  constructor(private apiUrl: string) {
    super('AnalyticsService');
    // Use only the analytics path segment - base URL already includes /api/v1
    this.analyticsUrl = '/analytics';
  }

  // Dashboard Operations
  async getDashboards(page: number = 1, limit: number = 10): Promise<PaginatedResponse<Dashboard>> {
    try {
      const response = await this.get<any>(
        `${this.analyticsUrl}/dashboards/`,
        { page, limit }
      );
      
      // Handle both array and paginated response formats
      if (Array.isArray(response)) {
        // Backend is returning array directly, wrap it
        return {
          results: response,
          count: response.length,
          next: null,
          previous: null
        };
      } else if (response.results) {
        // Already paginated
        return response as PaginatedResponse<Dashboard>;
      } else {
        // Unexpected format, return empty
        return {
          results: [],
          count: 0,
          next: null,
          previous: null
        };
      }
    } catch (error: any) {
      console.error('[AnalyticsService] Failed to fetch dashboards from API:', error);
      console.error('[AnalyticsService] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: `${this.analyticsUrl}/dashboards/`
      });
      // Don't use localStorage fallback - return empty if API fails
      console.error('[AnalyticsService] Returning empty dashboard list due to API error');
      return {
        results: [],
        count: 0,
        next: null,
        previous: null
      };
    }
  }

  async getDashboard(id: string): Promise<Dashboard> {
    // Special handling for impact-dashboard
    if (id === 'impact-dashboard') {
      const response = await this.get<any>(`${this.analyticsUrl}/dashboard/impact_dashboard/`);
      // Transform the impact dashboard response to match the Dashboard interface
      return {
        id: 'impact-dashboard',
        name: 'Impact Dashboard',
        type: 'impact',
        description: 'Real-time church impact metrics',
        church_id: response.layout?.church_id || null,
        created_at: new Date().toISOString(),
        updated_at: response.lastUpdated || new Date().toISOString(),
        created_by: null,
        layout: response.layout?.gridConfig || { rows: 2, columns: 2, items: [] },
        is_active: true,
        is_default: response.layout?.isDefault || false,
        is_suggested: false,
        display_order: 0,
        is_system: true,
        is_favorited: false,
        visualizations: [],
        items: []
      };
    }
    
    const response = await this.get<Dashboard>(`${this.analyticsUrl}/dashboards/${id}`);
    return response;
  }

  async getDefaultDashboard(churchId?: string): Promise<Dashboard> {
    // Use the impact dashboard endpoint instead of the non-existent default endpoint
    const response = await this.get<any>(`${this.analyticsUrl}/dashboard/impact_dashboard/`);
    
    // Transform the impact dashboard response to match the Dashboard interface
    return {
      id: 'impact-dashboard',
      name: 'Impact Dashboard',
      type: 'impact',
      isDefault: true,
      churchId: churchId || '',
      userId: '',
      config: {
        layout: response.layout || 'grid',
        theme: response.theme || 'light',
        refreshInterval: 30000,
        autoRefresh: true
      },
      widgets: response.widgets || [],
      visualizations: response.visualizations || [],
      createdAt: new Date(),
      updatedAt: new Date()
    };
  }

  async createDashboard(dashboard: any): Promise<Dashboard> {
    try {
      // Store visualizations separately - they need to be added after dashboard creation
      const visualizations = dashboard.visualizations || [];
      
      // Ensure all required fields are present
      const payload = {
        name: dashboard.name || dashboard.title || 'Untitled Dashboard',
        description: dashboard.description || '',
        type: dashboard.type || 'custom',
        church_id: dashboard.church_id,
        layout: dashboard.layout || { columns: 2 },
        is_active: dashboard.is_active !== false,
        is_default: dashboard.is_default || false
      };
      
      console.log('[AnalyticsService] Creating dashboard with payload:', payload);
      
      const response = await this.post<Dashboard>(`${this.analyticsUrl}/dashboards/`, payload);
      
      console.log('[AnalyticsService] Dashboard created successfully:', response);
      console.log('[AnalyticsService] Dashboard flags - is_system:', response.is_system, 'is_default:', response.is_default);
      
      // If visualizations were provided, add them to the dashboard
      if (visualizations.length > 0) {
        console.log('[AnalyticsService] Adding visualizations to dashboard:', visualizations);
        
        for (const viz of visualizations) {
          try {
            await this.post(`${this.analyticsUrl}/dashboards/${response.id}/add_visualization/`, {
              visualization: viz.visualization_id || viz.id,
              position: viz.position || {}
            });
          } catch (vizError) {
            console.error('[AnalyticsService] Failed to add visualization:', vizError);
            // Continue with other visualizations even if one fails
          }
        }
      }
      
      return response;
    } catch (error: any) {
      console.error('[AnalyticsService] Error creating dashboard:', error);
      console.error('[AnalyticsService] Error response:', error.response?.data);
      
      // Re-throw with more specific error message
      if (error.response?.data?.error?.message) {
        throw new Error(error.response.data.error.message);
      } else if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      } else if (error.response?.data) {
        // Try to extract field-specific errors
        const fieldErrors = Object.entries(error.response.data)
          .map(([field, errors]: [string, any]) => {
            if (Array.isArray(errors)) {
              return `${field}: ${errors.join(', ')}`;
            }
            return `${field}: ${errors}`;
          })
          .join('; ');
        throw new Error(fieldErrors || 'Failed to create dashboard');
      }
      
      throw error;
    }
  }

  async updateDashboard(id: string, updates: any): Promise<Dashboard> {
    try {
      // Ensure proper field mapping
      const payload: any = {};
      
      if (updates.name !== undefined || updates.title !== undefined) {
        payload.name = updates.name || updates.title;
      }
      if (updates.description !== undefined) {
        payload.description = updates.description;
      }
      if (updates.layout !== undefined) {
        payload.layout = updates.layout;
      }
      if (updates.visualizations !== undefined) {
        payload.visualizations = updates.visualizations;
      }
      if (updates.type !== undefined) {
        payload.type = updates.type;
      }
      if (updates.church_id !== undefined) {
        payload.church_id = updates.church_id;
      }
      if (updates.items !== undefined) {
        payload.items = updates.items;
      }
      
      console.log('[AnalyticsService] Updating dashboard with payload:', payload);
      console.log('[AnalyticsService] Dashboard ID:', id);
      
      const response = await this.put<Dashboard>(`${this.analyticsUrl}/dashboards/${id}/`, payload);
      
      console.log('[AnalyticsService] Dashboard updated successfully:', response);
      return response;
    } catch (error: any) {
      console.error('[AnalyticsService] Error updating dashboard:', error);
      console.error('[AnalyticsService] Error response:', error.response?.data);
      throw error;
    }
  }

  async deleteDashboard(id: string): Promise<void> {
    try {
      console.log('[AnalyticsService] Deleting dashboard with ID:', id);
      await this.delete(`${this.analyticsUrl}/dashboards/${id}/`);
      console.log('[AnalyticsService] Dashboard deleted successfully');
    } catch (error: any) {
      console.error('[AnalyticsService] Error deleting dashboard:', error);
      console.error('[AnalyticsService] Error response:', error.response?.data);
      throw error;
    }
  }

  async addVisualizationToDashboard(dashboardId: number, visualizationId: number, position: any): Promise<any> {
    try {
      console.log('[AnalyticsService] Adding visualization to dashboard:', { dashboardId, visualizationId, position });
      const response = await this.post(`${this.analyticsUrl}/dashboards/${dashboardId}/add_visualization/`, {
        visualization: visualizationId,
        position
      });
      console.log('[AnalyticsService] Visualization added successfully');
      return response;
    } catch (error: any) {
      console.error('[AnalyticsService] Error adding visualization:', error);
      console.error('[AnalyticsService] Error response:', error.response?.data);
      throw error;
    }
  }

  // Metric Operations
  async getMetrics(dashboardId?: string, timeRange?: TimeRange): Promise<Metric[]> {
    try {
      const params: any = {};
      if (dashboardId) params.dashboard_id = dashboardId;
      if (timeRange) {
        params.start_date = timeRange.startDate;
        params.end_date = timeRange.endDate;
      }
      const response = await this.get<any>(`${this.analyticsUrl}/metrics/`, params);
      
      // Check if response has results property (paginated) or is array
      if (response.results) {
        return response.results;
      } else if (Array.isArray(response)) {
        return response;
      } else {
        // If API returns empty or unexpected format, return empty array
        console.warn('[AnalyticsService] Unexpected metrics response format:', response);
        return [];
      }
    } catch (error) {
      console.error('[AnalyticsService] Failed to fetch metrics from API:', error);
      // Return empty array on error
      return [];
    }
  }

  async getMetric(id: string): Promise<Metric> {
    const response = await this.get<Metric>(`${this.analyticsUrl}/metrics/${id}`);
    return response;
  }

  // Report Operations
  async getReports(filters?: FilterCriteria): Promise<Report[]> {
    const response = await this.get<Report[]>(`${this.analyticsUrl}/reports/`, filters);
    return response;
  }

  async getReport(id: string): Promise<Report> {
    const response = await this.get<Report>(`${this.analyticsUrl}/reports/${id}`);
    return response;
  }

  async generateReport(config: any): Promise<Report> {
    const response = await this.post<Report>(`${this.analyticsUrl}/reports/generate/`, config);
    return response;
  }

  // Visualization Operations
  async getVisualizations(reportId?: string): Promise<DataVisualization[]> {
    try {
      const params = reportId ? { report_id: reportId } : {};
      const response = await this.get<any>(`${this.analyticsUrl}/visualizations/`, params);
      
      // Check if response has results property (paginated) or is array
      if (response.results) {
        return response.results;
      } else if (Array.isArray(response)) {
        return response;
      } else {
        console.warn('[AnalyticsService] Unexpected visualizations response format:', response);
        return [];
      }
    } catch (error) {
      console.error('[AnalyticsService] Failed to fetch visualizations from API:', error);
      return [];
    }
  }

  async getVisualization(id: string): Promise<DataVisualization> {
    const response = await this.get<DataVisualization>(`${this.analyticsUrl}/visualizations/${id}`);
    return response;
  }

  async createVisualization(visualization: Omit<DataVisualization, 'id' | 'createdAt' | 'updatedAt'>): Promise<DataVisualization> {
    const response = await this.post<DataVisualization>(`${this.analyticsUrl}/visualizations/`, visualization);
    return response;
  }

  async updateVisualization(id: string, updates: Partial<DataVisualization>): Promise<DataVisualization> {
    const response = await this.put<DataVisualization>(`${this.analyticsUrl}/visualizations/${id}/`, updates);
    return response;
  }

  async deleteVisualization(id: string): Promise<void> {
    await this.delete(`${this.analyticsUrl}/visualizations/${id}/`);
  }

  // Export Operations
  async exportData(format: 'csv' | 'excel' | 'pdf', data: any): Promise<Blob> {
    const response = await this.post<Blob>(
      `${this.analyticsUrl}/export/`,
      { format, data },
      { responseType: 'blob' }
    );
    return response;
  }

  async exportMembers(config: {
    format: 'csv' | 'excel' | 'json';
    filters: Record<string, any>;
  }): Promise<{ data: Blob; url: string }> {
    const response = await this.post<Blob>(
      `${this.analyticsUrl}/exports/members/`,
      config,
      { responseType: 'blob' }
    );
    const url = URL.createObjectURL(response);
    return { data: response, url };
  }

  async exportAttendance(config: {
    format: 'csv' | 'excel' | 'json';
    filters: Record<string, any>;
  }): Promise<{ data: Blob; url: string }> {
    const response = await this.post<Blob>(
      `${this.analyticsUrl}/exports/attendance/`,
      config,
      { responseType: 'blob' }
    );
    const url = URL.createObjectURL(response);
    return { data: response, url };
  }

  async exportGiving(config: {
    format: 'csv' | 'excel' | 'json';
    filters: Record<string, any>;
  }): Promise<{ data: Blob; url: string }> {
    const response = await this.post<Blob>(
      `${this.analyticsUrl}/exports/giving/`,
      config,
      { responseType: 'blob' }
    );
    const url = URL.createObjectURL(response);
    return { data: response, url };
  }

  async exportGeneral(exportType: string, config: {
    format: 'csv' | 'excel' | 'json';
    filters: Record<string, any>;
  }): Promise<{ data: Blob; url: string }> {
    const response = await this.post<Blob>(
      `${this.analyticsUrl}/exports/custom/`,
      { exportType, ...config },
      { responseType: 'blob' }
    );
    const url = URL.createObjectURL(response);
    return { data: response, url };
  }

  // Analytics Core Operations
  async getCorrelations(churchId: string, params: {
    metrics: string[];
    dateRange: { start: string; end: string };
    method?: 'pearson' | 'spearman' | 'kendall';
    segmentBy?: string;
  }): Promise<any> {
    const response = await this.post(
      `${this.analyticsUrl}/churches/${churchId}/correlations`,
      params
    );
    return response;
  }

  async getTrends(churchId: string, params: {
    metric: string;
    period: 'daily' | 'weekly' | 'monthly';
    dateRange: { start: string; end: string };
    includeForecast?: boolean;
    forecastPeriods?: number;
    detectAnomalies?: boolean;
  }): Promise<any> {
    const response = await this.post(
      `${this.analyticsUrl}/churches/${churchId}/trends/`,
      params
    );
    return response;
  }

  async getSegmentation(churchId: string, params: {
    segmentationType: string;
    includeTransitions?: boolean;
    asOfDate: string;
  }): Promise<any> {
    const response = await this.get(
      `${this.analyticsUrl}/churches/${churchId}/segments`,
      params
    );
    return response;
  }

  async performHypothesisTest(churchId: string, params: {
    testType: string;
    groupA?: any;
    groupB?: any;
    groups?: any[];
    metric: string;
    paired?: boolean;
  }): Promise<any> {
    const response = await this.post(
      `${this.analyticsUrl}/churches/${churchId}/hypothesis-test`,
      params
    );
    return response;
  }

  async getEngagementSnapshots(churchId: string, params?: {
    date?: string;
    minRiskScore?: number;
  }): Promise<any> {
    const response = await this.get(
      `${this.analyticsUrl}/churches/${churchId}/engagement-snapshots`,
      params
    );
    return response;
  }

  async getAnalyticsSummary(churchId: string): Promise<any> {
    const response = await this.get(
      `${this.analyticsUrl}/churches/${churchId}/summary`
    );
    return response;
  }

  // Impact Dashboard Operations
  async getImpactDashboard(params?: { force_refresh?: boolean }): Promise<any> {
    try {
      // Use POST with force_refresh parameter if needed to bypass backend cache
      if (params?.force_refresh) {
        const response = await this.post(`${this.analyticsUrl}/dashboard/impact_dashboard/`, params);
        return response;
      }
      // Default GET request for normal loading
      const response = await this.get(`${this.analyticsUrl}/dashboard/impact_dashboard/`);
      return response;
    } catch (error: any) {
      // Preserve the backend's error message for church selection
      if (error?.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  }

  async saveDashboardLayout(layoutData: any): Promise<any> {
    const response = await this.post(`${this.analyticsUrl}/dashboard/layouts`, layoutData);
    return response;
  }

  async getWidgetData(widgetId: string, params?: any): Promise<any> {
    const response = await this.post(
      `${this.analyticsUrl}/dashboard/widgets/${widgetId}/data`,
      params || {}
    );
    return response;
  }

  async updateGoalProgress(goalId: string, progress: { currentValue: number; notes?: string }): Promise<any> {
    const response = await this.put(
      `${this.analyticsUrl}/dashboard/goals/${goalId}/progress`,
      {
        goalId,
        ...progress
      }
    );
    return response;
  }

  async getAvailableWidgets(): Promise<any> {
    const response = await this.get(`${this.analyticsUrl}/dashboard/widgets_available`);
    return response;
  }

  async generateExecutiveReport(config: any): Promise<any> {
    const response = await this.post(
      `${this.analyticsUrl}/dashboard/reports/generate`,
      config
    );
    return response;
  }

  // Analysis Operations (for Analytics Page)
  async getAnalysisResults(filters?: any): Promise<any> {
    try {
      const response = await this.get<any>(`${this.analyticsUrl}/analysis-results/`, filters);
      
      // Check if response has results property (paginated) or is array
      if (response.results) {
        return response;
      } else if (Array.isArray(response)) {
        // Wrap array in expected format
        return { results: response };
      } else {
        console.warn('[AnalyticsService] Unexpected analysis results format:', response);
        return { results: [] };
      }
    } catch (error) {
      console.error('[AnalyticsService] Failed to fetch analysis results from API:', error);
      return { results: [] };
    }
  }
  
  // Dashboard favoriting methods
  async getFeaturedDashboards(): Promise<FeaturedDashboardsResponse> {
    try {
      const response = await this.get<FeaturedDashboardsResponse>(
        `${this.analyticsUrl}/dashboards/featured/`
      );

      return {
        results: response.results || [],
        total_count: response.total_count || 0
      };
    } catch (error) {
      console.error('[AnalyticsService] Error fetching featured dashboards:', error);
      throw error;
    }
  }

  async getMyDashboards(): Promise<PaginatedResponse<Dashboard>> {
    try {
      const response = await this.get<any>(
        `${this.analyticsUrl}/dashboards/my_dashboards/`
      );

      return {
        results: response.results || [],
        count: response.count || response.results?.length || 0,
        next: response.next || null,
        previous: response.previous || null,
      };
    } catch (error) {
      console.error('[AnalyticsService] Error fetching my dashboards:', error);
      throw error;
    }
  }

  async favoriteDashboard(dashboardId: number): Promise<{ status: string; message: string }> {
    try {
      const response = await this.post<{ status: string; message: string }>(
        `${this.analyticsUrl}/dashboards/${dashboardId}/favorite/`,
        {}
      );
      return response;
    } catch (error) {
      console.error('[AnalyticsService] Error favoriting dashboard:', error);
      throw error;
    }
  }

  async unfavoriteDashboard(dashboardId: number): Promise<{ status: string; message: string }> {
    try {
      const response = await this.delete<{ status: string; message: string }>(
        `${this.analyticsUrl}/dashboards/${dashboardId}/unfavorite/`
      );
      return response;
    } catch (error) {
      console.error('[AnalyticsService] Error unfavoriting dashboard:', error);
      throw error;
    }
  }

  async getVisualizationData(vizId: string, dateRange?: TimeRange): Promise<any> {
    try {
      const response = await this.post<{ status: string; data: any }>(
        `${this.analyticsUrl}/visualizations/${vizId}/get_data/`,
        {
          date_range: dateRange ? {
            start_date: dateRange.startDate,
            end_date: dateRange.endDate
          } : undefined
        }
      );
      return response.data;
    } catch (error) {
      console.error('[AnalyticsService] Error fetching visualization data:', error);
      throw error;
    }
  }

  // Giving Operations - OPTIMIZATION: Extending existing service
  async importGiving(data: {
    church_id?: string;
    data: Array<{
      date: string;
      amount: number;
      method?: string;
      fund?: string;
    }>;
  }): Promise<{ status: string; message: string; records_imported: number }> {
    try {
      // Get church ID from context if not provided
      const churchId = data.church_id || localStorage.getItem('selectedChurchId');
      if (!churchId) {
        throw new Error('Church ID is required for importing giving data');
      }

      // Use the new time-series import endpoint
      const response = await this.post<{ status: string; message: string; records_imported: number }>(
        `${this.analyticsUrl}/churches/${churchId}/time-series/import/`,
        {
          data_type: 'giving',
          data: data.data
        }
      );
      return response;
    } catch (error) {
      console.error('[AnalyticsService] Error importing giving data:', error);
      throw error;
    }
  }

  // Optimized aggregation endpoints for giving analysis
  async getGivingPatterns(churchId: string, days: number = 30): Promise<any> {
    try {
      const response = await this.get<any>(
        `${this.analyticsUrl}/churches/${churchId}/time-series/aggregations/giving-patterns/?days=${days}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching giving patterns:', error);
      throw error;
    }
  }

  async getPerCapitaAnalysis(churchId: string, days: number = 30): Promise<any> {
    try {
      const response = await this.get<any>(
        `${this.analyticsUrl}/churches/${churchId}/time-series/aggregations/per-capita/?days=${days}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching per capita analysis:', error);
      throw error;
    }
  }

  async getWeeklyAnalysis(churchId: string, weeks: number = 12): Promise<any> {
    try {
      const response = await this.get<any>(
        `${this.analyticsUrl}/churches/${churchId}/time-series/aggregations/weekly/?weeks=${weeks}`
      );
      return response;
    } catch (error) {
      console.error('Error fetching weekly analysis:', error);
      throw error;
    }
  }

  async getGivingMetrics(churchId: string, params?: {
    dateRange?: { start: string; end: string };
    groupBy?: 'day' | 'week' | 'month';
  }): Promise<any> {
    try {
      // Query TimeSeriesMetric data for giving
      const queryParams: any = {
        church_id: churchId,
        category: 'giving',
        metric_name__startswith: 'giving_'
      };
      
      if (params?.dateRange) {
        queryParams.timestamp__gte = params.dateRange.start;
        queryParams.timestamp__lte = params.dateRange.end;
      }
      
      const response = await this.get<any>(
        `${this.analyticsUrl}/metrics/`,
        queryParams
      );
      
      return response;
    } catch (error) {
      console.error('[AnalyticsService] Error fetching giving metrics:', error);
      throw error;
    }
  }

  async getGivingByFund(churchId: string, params?: {
    dateRange?: { start: string; end: string };
  }): Promise<any> {
    try {
      // Aggregate giving data by fund from TimeSeriesMetric
      const queryParams: any = {
        church_id: churchId,
        category: 'giving',
        metric_name__startswith: 'giving_',
        aggregate_by: 'dimensions__fund'
      };
      
      if (params?.dateRange) {
        queryParams.timestamp__gte = params.dateRange.start;
        queryParams.timestamp__lte = params.dateRange.end;
      }
      
      const response = await this.get<any>(
        `${this.analyticsUrl}/metrics/aggregate/`,
        queryParams
      );
      
      return response;
    } catch (error) {
      console.error('[AnalyticsService] Error fetching giving by fund:', error);
      throw error;
    }
  }
  
  // Health Configuration Operations
  async getHealthConfigurations(params?: {
    church_id?: string;
    ministry_id?: string;
  }): Promise<PaginatedResponse<any>> {
    try {
      const response = await this.get<any>(
        `${this.analyticsUrl}/health-configurations/`,
        params
      );
      
      // Handle both array and paginated response formats
      if (Array.isArray(response)) {
        return {
          results: response,
          count: response.length,
          next: null,
          previous: null
        };
      } else if (response.results) {
        return response as PaginatedResponse<any>;
      } else {
        return {
          results: [],
          count: 0,
          next: null,
          previous: null
        };
      }
    } catch (error) {
      console.error('[AnalyticsService] Error fetching health configurations:', error);
      throw error;
    }
  }
  
  async getHealthConfiguration(id: string): Promise<any> {
    try {
      return await this.get<any>(`${this.analyticsUrl}/health-configurations/${id}/`);
    } catch (error) {
      console.error('[AnalyticsService] Error fetching health configuration:', error);
      throw error;
    }
  }
  
  async createHealthConfiguration(config: {
    church_id: string;
    ministry_id?: string;
    name: string;
    description: string;
    components: Array<{
      metric_id: string;
      weight: number;
      display_name: string;
      thresholds?: any;
    }>;
    visibility: 'private' | 'ministry' | 'church';
  }): Promise<any> {
    try {
      return await this.post<any>(`${this.analyticsUrl}/health-configurations/`, config);
    } catch (error) {
      console.error('[AnalyticsService] Error creating health configuration:', error);
      throw error;
    }
  }
  
  async updateHealthConfiguration(id: string, updates: Partial<any>): Promise<any> {
    try {
      return await this.patch<any>(`${this.analyticsUrl}/health-configurations/${id}/`, updates);
    } catch (error) {
      console.error('[AnalyticsService] Error updating health configuration:', error);
      throw error;
    }
  }
  
  async deleteHealthConfiguration(id: string): Promise<void> {
    try {
      await this.delete(`${this.analyticsUrl}/health-configurations/${id}/`);
    } catch (error) {
      console.error('[AnalyticsService] Error deleting health configuration:', error);
      throw error;
    }
  }
  
  async setDefaultHealthConfiguration(id: string): Promise<any> {
    try {
      return await this.post<any>(`${this.analyticsUrl}/health-configurations/${id}/set_default/`, {});
    } catch (error) {
      console.error('[AnalyticsService] Error setting default health configuration:', error);
      throw error;
    }
  }
  
  async previewHealthConfiguration(id: string): Promise<{
    score: number;
    trend: string;
    components: any[];
  }> {
    try {
      return await this.get<any>(`${this.analyticsUrl}/health-configurations/${id}/preview/`);
    } catch (error) {
      console.error('[AnalyticsService] Error previewing health configuration:', error);
      throw error;
    }
  }
  
  async getAvailableMetricsForHealth(params: {
    church_id: string;
    ministry_id?: string;
  }): Promise<any> {
    try {
      return await this.get<any>(`${this.analyticsUrl}/health-configurations/available_metrics/`, params);
    } catch (error) {
      console.error('[AnalyticsService] Error fetching available metrics:', error);
      throw error;
    }
  }
  
  // Alert Analysis Methods
  async analyzeAlert(alert: any, forceRefresh: boolean = false): Promise<any> {
    try {
      return await this.post<any>(`${this.analyticsUrl}/alerts/analysis/analyze/`, { 
        alert,
        force_refresh: forceRefresh 
      });
    } catch (error) {
      console.error('[AnalyticsService] Error analyzing alert:', error);
      throw error;
    }
  }
  
  async getAlertAnalysis(alertType: string, entityId: string, forceRefresh: boolean = false): Promise<any> {
    try {
      const params: any = {
        type: alertType,
        entityId: entityId
      };
      
      if (forceRefresh) {
        params.force_refresh = 'true';
      }
      
      return await this.get<any>(`${this.analyticsUrl}/alerts/analysis/by_type/`, params);
    } catch (error) {
      console.error('[AnalyticsService] Error fetching alert analysis:', error);
      throw error;
    }
  }
  
  async getRecentAlerts(limit: number = 5): Promise<{
    alerts: any[];
    total: number;
  }> {
    try {
      return await this.get<any>(`${this.analyticsUrl}/alerts/analysis/recent/`, { limit });
    } catch (error) {
      console.error('[AnalyticsService] Error fetching recent alerts:', error);
      throw error;
    }
  }

  // Time Series Operations - REQUIRED: Real data endpoint
  async getTimeSeries(params: {
    church_id?: string;
    metric_name: string;
    start_date: string;
    end_date: string;
    category?: string;
  }): Promise<any[]> {
    try {
      // Get church ID from params or context
      const churchId = params.church_id || localStorage.getItem('selectedChurchId');
      if (!churchId) {
        throw new Error('Church ID is required for time series data');
      }

      const queryParams: any = {
        metric_name: params.metric_name,
        start_date: params.start_date,
        end_date: params.end_date
      };

      if (params.category) {
        queryParams.category = params.category;
      }

      const response = await this.get<any>(
        `${this.analyticsUrl}/churches/${churchId}/time-series/`,
        queryParams
      );

      // Return the data array directly
      return response.results || response || [];
    } catch (error) {
      console.error('[AnalyticsService] Error fetching time series data:', error);
      // Return empty array instead of throwing to prevent pages from breaking
      return [];
    }
  }
}